import { useRef, useState } from 'react'
import { useFrame, useThree, extend } from '@react-three/fiber'
import { shaderMaterial, Text } from '@react-three/drei'
import * as THREE from 'three'

const OrbMaterial = shaderMaterial(
  { time: 0 },
  // vertex
  `varying vec2 vUv;
   varying vec3 vNormal;
   varying vec3 vViewDir;
   void main() {
     vUv = uv;
     vNormal = normalize(normalMatrix * normal);
     vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
     vViewDir = normalize(-mvPos.xyz);
     gl_Position = projectionMatrix * mvPos;
   }`,
  // fragment — electric energy orb
  `uniform float time;
   varying vec2 vUv;
   varying vec3 vNormal;
   varying vec3 vViewDir;

   float hash(vec2 p) {
     return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
   }
   float noise(vec2 p) {
     vec2 i = floor(p); vec2 f = fract(p);
     f = f * f * (3.0 - 2.0 * f);
     return mix(
       mix(hash(i), hash(i + vec2(1,0)), f.x),
       mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), f.x),
       f.y
     );
   }
   float fbm(vec2 p) {
     float v = 0.0; float a = 0.5;
     for (int i = 0; i < 6; i++) {
       v += a * noise(p);
       p = p * 2.1 + vec2(1.7 * float(i+1), 9.2 * float(i+1));
       a *= 0.5;
     }
     return v;
   }

   void main() {
     vec2 uv = vUv * 3.0;

     float n1 = fbm(uv + vec2(time * 0.4, time * 0.3));
     float n2 = fbm(uv * 2.0 - vec2(time * 0.5, time * 0.2) + n1 * 1.5);
     float n3 = fbm(uv * 0.8 + vec2(time * 0.15) + n2 * 0.8);
     float electric = clamp(pow(n2, 2.5) + pow(n3, 3.0) * 0.5, 0.0, 1.0);

     float fresnel = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), 1.5);

     vec3 dark   = vec3(0.0,  0.02, 0.18);
     vec3 mid    = vec3(0.05, 0.3,  1.0);
     vec3 bright = vec3(0.4,  0.8,  1.0);
     vec3 core   = vec3(0.9,  0.97, 1.0);

     vec3 color = mix(dark, mid, electric);
     color = mix(color, bright, pow(electric, 2.0));
     color = mix(color, core,   pow(electric, 5.0));
     color += vec3(0.1, 0.4, 1.0) * fresnel * 2.0;

     float alpha = clamp(0.55 + electric * 0.45 + fresnel * 0.25, 0.0, 1.0);
     gl_FragColor = vec4(color, alpha);
   }`
)

extend({ OrbMaterial })

const TRIGGER_DISTANCE = 3

const Portal = ({ position, url }) => {
  const orbRef = useRef()
  const groupRef = useRef()
  const triggered = useRef(false)
  const [showLabel, setShowLabel] = useState(false)
  const { camera } = useThree()
  const portalVec = new THREE.Vector3(...position)

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()

    if (orbRef.current) orbRef.current.time = t
    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(t * 0.8) * 0.3
      groupRef.current.rotation.y = t * 0.3
    }
    const dist = camera.position.distanceTo(portalVec)
    setShowLabel(dist < 10)

    if (!triggered.current && dist < TRIGGER_DISTANCE) {
      triggered.current = true
      window.location.href = url
      setTimeout(() => { triggered.current = false }, 3000)
    }
  })

  return (
    <group ref={groupRef} position={position}>
      {/* outer electric shell */}
      <mesh>
        <sphereGeometry args={[1.2, 64, 64]} />
        <orbMaterial ref={orbRef} transparent side={THREE.DoubleSide} depthWrite={false} toneMapped={false} />
      </mesh>

      {/* lights */}
      {showLabel && (
        <Text
          position={[0, 2, 0]}
          fontSize={0.3}
          color='#00eeff'
          anchorX='center'
          anchorY='middle'
          outlineColor='#000'
          outlineWidth={0.02}
        >
          {`travel to ${url.replace('https://', '')}`}
        </Text>
      )}
      <pointLight color='#00ccff' intensity={4} distance={8} />
      <pointLight color='#00eeff' intensity={2} distance={3} />
    </group>
  )
}

export default Portal
